import React, { useRef } from 'react'
import cls from 'classnames';
import { formatBlurredValue, TextInput, TextInputProps } from '../text_input';
import styles from './field.module.scss';

type Style = React.CSSProperties;

export interface FieldProps extends TextInputProps {
  label: string | React.ReactNode;
  fullWidth?: boolean;
  required?: boolean;
  containerStyle?: Style;
  containerClassName?: string;
  labelClassName?: string;
  component?: JSX.Element;
  labelAlignment?: 'column' | 'row';
  onDateValueChange?: (date: Date) => void;
  dateValue?: string | Date;

  /**
   * Whether the render value as static and ineditable.
   * 
   * Unlike `readOnly`, setting this flag does not render a text input.
   */
  static?: boolean;

  /**
   * Rendered below the field.
   */
  sub?: JSX.Element;

  /**
   * Formats the value, visible when blurred.
   * 
   * If `true`, and `allow` is `numeric`, then the numeric value is formatted.
   */
  formatBlurredValue?: true | ((value: string | string[] | number) => string);

  /**
   * Whether to format the value, if blurred, and if the value evaluates to false.
   */
  formatBlurredEmptyValue?: boolean;
}

/**
 * @returns Props to spread over the input.
 */
function getRestProps(props: FieldProps) {
  let restProps = { ...props }
  delete restProps.component
  delete restProps.label;
  delete restProps.fullWidth;
  delete restProps.containerStyle;
  delete restProps.containerClassName;
  delete restProps.onDateValueChange;
  delete restProps.nodeRef;
  delete restProps.labelAlignment;
  delete restProps.static;
  restProps.className = cls({
    [restProps.className]: true,  //  TODO: fix class="undefined"
    "full-w": props.fullWidth
  })
  return restProps
}

const defaultMaxLength = 255

/**
 * Renders a label and either a static or editable value.
 */
export const Field: React.FC<FieldProps> = ({ id, labelClassName, ...props }) => {
  let CustomComponent = props.component;

  //  Auto-generated ID for htmlFor support
  const autoId = useRef<string>('f-' + Date.now() + Math.floor(100000 * Math.random()));

  if (!id) {
    id = autoId.current;
  }

  return (
    <div className={cls([
      styles.field,
      props.containerClassName
    ])}
      data-label-align={props.labelAlignment}
      style={props.containerStyle}>
      <div className={cls([styles.labelContainer, labelClassName])}>
        <label htmlFor={id}>{props.label}</label>
      </div>
      {
        CustomComponent ? CustomComponent : (
          props.static ? (
            <p className={cls([props.className, "static-value"])}>
              {formatBlurredValue(getRestProps(props))}
            </p>
          ) : (
              <TextInput
                type={props.type || "text"}
                id={id}
                {...getRestProps(props)}
                maxLength={props.maxLength === undefined ? defaultMaxLength : props.maxLength}
                nodeRef={props.nodeRef} />
            )
        )
      }
      {props.sub ? (<p className="sub">{props.sub}</p>) : null}
      {
        props.children
      }
    </div>
  )
}